import React from 'react';
import Icon from 'components/AppIcon';

const BusinessModelSlide = () => {
  const revenueStreams = [
    {
      icon: 'CreditCard',
      title: 'Subscription Revenue',
      description: 'Monthly/annual subscriptions from law firms',
      plans: [
        { name: 'Basic', price: 'KES 5,000', features: 'Up to 5 lawyers, basic features' },
        { name: 'Professional', price: 'KES 10,000', features: 'Up to 20 lawyers, advanced features' },
        { name: 'Enterprise', price: 'Custom', features: 'Unlimited lawyers, full features' }
      ],
      potential: 'KES 400M+ annually'
    },
    {
      icon: 'Percent',
      title: 'Transaction Fees',
      description: '3% commission on payments processed through platform',
      details: [
        'Client payments to lawyers',
        'Automated invoicing',
        'Secure payment processing'
      ],
      potential: 'KES 150M+ annually'
    },
    {
      icon: 'BookOpen',
      title: 'Value-Added Services',
      description: 'Premium services and add-ons',
      services: [
        'Legal document templates',
        'Training and certification programs',
        'Advanced analytics and reporting',
        'Custom integrations'
      ],
      potential: 'KES 50M+ annually'
    }
  ];

  const marketPenetration = [
    {
      year: 'Year 1',
      lawyers: 500,
      clients: '10,000',
      revenue: 'KES 50M',
      focus: 'Market penetration & product validation'
    },
    {
      year: 'Year 2',
      lawyers: 2000,
      clients: '50,000',
      revenue: 'KES 200M',
      focus: 'Feature expansion & geographic growth'
    },
    {
      year: 'Year 3',
      lawyers: 5000,
      clients: '150,000',
      revenue: 'KES 500M',
      focus: 'Market leadership & regional expansion'
    }
  ];

  const costStructure = [
    {
      category: 'Technology & Development',
      percentage: 35,
      description: 'Platform development, maintenance, and infrastructure'
    },
    {
      category: 'Sales & Marketing',
      percentage: 25,
      description: 'Customer acquisition and brand building'
    },
    {
      category: 'Operations',
      percentage: 20,
      description: 'Customer support, legal compliance, and administration'
    },
    {
      category: 'General & Administrative',
      percentage: 20,
      description: 'Management, finance, and corporate functions'
    }
  ];

  return (
    <div className="py-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-heading font-bold text-text-primary mb-4">
            Business Model
          </h1>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Multiple revenue streams with strong unit economics and scalable growth potential
          </p>
        </div>

        {/* Revenue Streams */}
        <div className="mb-12">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-8 text-center">
            Revenue Streams
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {revenueStreams?.map((stream, index) => (
              <div key={index} className="bg-surface rounded-lg p-6 shadow-card border border-border">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <Icon name={stream?.icon} size={24} className="text-primary" />
                  </div>
                  <h3 className="text-xl font-heading font-bold text-text-primary">
                    {stream?.title}
                  </h3>
                </div>
                
                <p className="text-text-secondary mb-4">
                  {stream?.description}
                </p>
                
                {stream?.plans && (
                  <div className="space-y-3 mb-4">
                    {stream.plans.map((plan, planIndex) => (
                      <div key={planIndex} className="bg-primary-50 rounded-lg p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-semibold text-text-primary">{plan?.name}</div>
                            <div className="text-sm text-text-secondary">{plan?.features}</div>
                          </div>
                          <div className="font-bold text-primary">{plan?.price}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {stream?.details && (
                  <ul className="space-y-2 mb-4">
                    {stream.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-center space-x-2 text-sm text-text-secondary">
                        <Icon name="Check" size={14} className="text-success-600" />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                )}
                
                {stream?.services && (
                  <ul className="space-y-2 mb-4">
                    {stream.services.map((service, serviceIndex) => (
                      <li key={serviceIndex} className="flex items-center space-x-2 text-sm text-text-secondary">
                        <Icon name="Check" size={14} className="text-success-600" />
                        <span>{service}</span>
                      </li>
                    ))}
                  </ul>
                )}
                
                <div className="bg-success-50 text-success-600 px-3 py-2 rounded-lg text-sm font-medium text-center">
                  {stream?.potential}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Market Penetration Strategy */}
        <div className="mb-12">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-8 text-center">
            3-Year Growth Projection
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {marketPenetration?.map((year, index) => (
              <div key={index} className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-lg p-6">
                <h3 className="text-xl font-heading font-bold text-primary mb-4">
                  {year?.year}
                </h3>
                <div className="space-y-3 mb-4">
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Lawyers</span>
                    <span className="font-semibold text-text-primary">{year?.lawyers?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Clients</span>
                    <span className="font-semibold text-text-primary">{year?.clients}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Revenue</span>
                    <span className="font-bold text-primary">{year?.revenue}</span>
                  </div>
                </div>
                <p className="text-sm text-text-secondary">
                  {year?.focus}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Cost Structure */}
        <div className="bg-surface rounded-lg p-8 shadow-card border border-border">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-8 text-center">
            Cost Structure
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              {costStructure?.map((cost, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-primary-100 rounded-lg flex items-center justify-center">
                    <span className="text-lg font-bold text-primary">{cost?.percentage}%</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-text-primary">
                      {cost?.category}
                    </h3>
                    <p className="text-sm text-text-secondary">
                      {cost?.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-center">
              <div className="text-center">
                <div className="text-4xl font-heading font-bold text-primary mb-2">85%</div>
                <p className="text-text-secondary mb-4">Gross margin target</p>
                <div className="text-2xl font-heading font-bold text-success-600 mb-2">18 months</div>
                <p className="text-text-secondary">Break-even timeline</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessModelSlide;